import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { ehIOS } from '../utils/instalarApp';
import './ModalDeAvisos.css';

/**
 * O que aparece depois de ligar os avisos.
 *
 * ── Por que ele existe ────────────────────────────────────────────────────
 *
 * Relato dela, com a tela do Android na mão: "tive que fazer esse
 * gerenciamento, acho q seria importante avisar pra nao esquecer de clicar em
 * gerenciar as notificações e habilitar essas coisas".
 *
 * A permissão do navegador faz o aviso CHEGAR. Ela não decide se ele toca, se
 * aparece por cima da tela ou se vibra — isso é o canal de notificação do
 * sistema, e em vários aparelhos (Samsung especialmente) ele nasce silencioso.
 * O resultado é o pior possível: a pessoa liga, não vê nada acontecer, e
 * conclui que não funciona.
 *
 * **Não existe API para mudar aquilo.** Um site não configura o canal de
 * notificação do Android nem os alertas do iOS — e é bom que não configure.
 * O que dá é dizer onde fica.
 *
 * ── Por que não está junto do convite de instalar ────────────────────────
 *
 * São dois momentos diferentes com duas ações diferentes, e juntá-los faria um
 * texto que ela pularia. Este só aparece depois de ligar, e diz uma coisa só.
 */

const ModalDeAvisos: React.FC<{
  /** O teste saiu do servidor? `false` = ninguém recebeu. */
  enviado: boolean;
  onFechar: () => void;
}> = ({ enviado, onFechar }) => {
  const caixa = useRef<HTMLDivElement>(null);
  useTravarScroll();
  useFocoNoModal(caixa);
  useFechavelComVoltar(true, onFechar);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);

  const ios = ehIOS();

  const corpo = (
    <div className="ma-veu" onClick={onFechar} ref={caixa}>
      <div className="ma-caixa" onClick={e => e.stopPropagation()}
           role="dialog" aria-modal="true" aria-labelledby="ma-titulo">
        <h2 className="ma-titulo" id="ma-titulo">
          {enviado ? 'Avisos ligados' : 'Ligado, mas o teste não saiu'}
        </h2>

        <p className="ma-texto">
          {enviado
            ? 'Acabei de mandar um aviso de teste.'
            : 'Nenhum aparelho recebeu o teste. Tente desligar e ligar de novo.'}
        </p>

        {/*
          O passo a passo só aparece quando o teste SAIU.
          
          Se ele não saiu, o problema é outro — mandar ela mexer nas
          configurações do sistema seria empurrá-la para o lugar errado.
        */}
        {enviado && (
          <>
            <p className="ma-texto ma-destaque">
              Não chegou, ou chegou sem som?
            </p>
            {ios ? (
              <ol className="ma-passos">
                <li>Ajustes → <strong>Notificações</strong></li>
                <li>Procure <strong>FinTracking</strong> na lista</li>
                <li>
                  Ligue <strong>Permitir notificações</strong> e marque
                  {' '}<strong>Sons</strong> e <strong>Avisos</strong>.
                </li>
              </ol>
            ) : (
              <ol className="ma-passos">
                <li>Configurações → <strong>Notificações</strong> → <strong>FinTracking</strong></li>
                <li>Escolha <strong>Alerta</strong> em vez de Silencioso</li>
                <li>
                  Ligue <strong>Exibir como pop-up</strong>, o <strong>som</strong>
                  {' '}e a <strong>vibração</strong>.
                </li>
              </ol>
            )}
            <p className="ma-nota">
              Isso é do sistema, não do app: nenhum site consegue mudar sozinho.
            </p>
          </>
        )}

        <button className="ma-btn" onClick={onFechar}>Entendi</button>
      </div>
    </div>
  );

  return createPortal(corpo, document.body);
};

export default ModalDeAvisos;
